import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { buildProviders, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import isNil from 'lodash-es/isNil';
import { filter, merge, Observable, of, Subscription } from 'rxjs';
import { skip, startWith, tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import {
    SharedAdvancedRuleComputationsDialogComponent,
    SharedAdvancedRuleComputationsDialogData
} from 'src/app/_shared/dialogs/shared-advanced-rules/shared-advanced-rule-computations-dialog/shared-advanced-rule-computations-dialog.component';
import {
    AdvancedRuleService,
    AdvancedRule,
    CritereModel,
    ParameterModel,
    TypesOperateur
} from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { arrayEmpty, arrayNotEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import {
    KeyValueModelDto,
    MoteurOperateurDto,
    MoteurRegleTypeDateRefDto,
    ParametreRegleAvanceeModelDto,
    TypesRegleEnum
} from 'src/graphql/generated-types';
import {
    FilteredComparisonOperatorListPipe
} from 'src/app/_shared/pipes/filtered-comparison-operator-list.pipe';
import {
    IsEqualOperatorPipe
} from 'src/app/_shared/pipes/is-equal-operator.pipe';
import { constants } from 'src/environments/constants';
import { guid } from 'src/app/utils/guid.utils';

export type indexToNumberTuple = [index: number, value: number];

export type FormAdvancedRule = AdvancedRule & {
    ParentheseOuvrante: string,
    ParentheseFermante: string,
    JourDuMois: { day: number, month: number }
};

@Component({
    selector: 'app-shared-advanced-rule-edit-table',
    templateUrl: './shared-advanced-rule-edit-table.component.html',
    styleUrls: ['./shared-advanced-rule-edit-table.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAdvancedRuleEditTableComponent),
    standalone: false
})
export class SharedAdvancedRuleEditTableComponent extends CustomFormControlAccess implements OnInit {

    ruleFormArray: FormArray;

    indexForParameterRow: number = null;

    criteriaList: CritereModel[] = [];

    operatorList: MoteurOperateurDto[] = [];

    ruleParamList: ParametreRegleAvanceeModelDto[] = [];

    applicationRuleList: KeyValueModelDto[] = [];

    dateTypeList: MoteurRegleTypeDateRefDto[] = [];

    currentRuleTypeOperatorList: MoteurOperateurDto[] = [];

    logicOperatorList: MoteurOperateurDto[] = [];

    comparisonOperatorList: MoteurOperateurDto[] = [];

    showParameters = false;

    readonly columns: Record<keyof AbstractControl & AdvancedRule, string> = {
        IdOperateur: 'Et/Ou',
        ParentheseOuvrante: '(',
        IdCritere: 'Critère',
        IdOperateurMolecule: 'Opérateur',
        Valeur: 'Valeur',
        Subvention: 'Subvention',
        SubventionDefaut: 'Subvention par défaut',
        SubventionGroupe: 'Subvention groupe',
        SubventionDefautGroupe: 'Subvention déf groupe',
        ParentheseFermante: ')',
        IdRegleApplication: 'S\'applique à',
        IdRegleNonApplication: 'Ne s\'applique pas à',
        IdTypeDate: 'Type date référence'
    };

    displayedColumns: string[] = [
        'IdOperateur',
        'ParentheseOuvrante',
        'IdCritere',
        'IdOperateurMolecule',
        'Valeur',
        'ParentheseFermante'
    ];

    readonly parametersColumns: KalTableHeader<ParametreRegleAvanceeModelDto & ParameterModel>[] = [
        {code: 'Libelle', label: 'Libellé paramètre'},
        {code: 'IdOperateur', label: 'Opérateur'},
        {code: 'IdChamp', label: 'Valeur'},
    ];

    errorMessages: Record<string, string> = {
        novalue: 'Veuillez saisir une valeur (ex : 20) ou un calcul avancé (ex : Nb enfants x 20) pour le critère.',
        nooperatorvalue: 'Veuillez sélectionner un opérateur de critère (ex : =, <, >=).',
        nocriteria: 'Veuillez sélectionner un critère (ex: Contrat).',
        nosubvention: 'Veuillez saisir une valeur ou un calcul de subvention.',
        nocriteriavalue: 'Veuillez sélectionner un critère, un opérateur et saisir une valeur',
        emptyadvancedrule: 'Veuillez ajouter au moins une règle.'
    };

    private _ruletype: TypesRegleEnum;

    private subscriptionList: Subscription[] = [];

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly dialogService: KalDialogService,
                private readonly advancedRuleService: AdvancedRuleService) {
        super();
    }

    get ruleLineFormGroupList(): FormGroup[] {
        return this.ruleFormArray.controls as FormGroup[];
    }

    get ruleType(): TypesRegleEnum {
        return this._ruletype;
    }

    private setFilteredCriteriaList(): void {
        this.criteriaList = this.advancedRuleService.criteriaList.filter(c =>
            c.IsCritere && this.isCriterionOfCurrentRuleType(c)
        );
    }

    @Input()
    set ruleType(type: TypesRegleEnum) {
        this._ruletype = type;

        this.regenerateOperatorListsByRuleType();

        // On regénère aussi la liste des critères
        this.setFilteredCriteriaList();

        this.displayedColumns = [
            'IdOperateur',
            'ParentheseOuvrante',
            'IdCritere',
            'IdOperateurMolecule',
            'Valeur',
            ...type === TypesRegleEnum.Subvention ? [
                'Subvention',
                'SubventionDefaut',
                'ParentheseFermante',
                'SubventionGroupe',
                'SubventionDefautGroupe'
            ] : [
                'ParentheseFermante'
            ],
            ...[TypesRegleEnum.Accessibilite, TypesRegleEnum.Subvention].includes(type) ? [] : [
                'IdRegleApplication',
                'IdRegleNonApplication',
            ],
            'IdTypeDate',
        ];
        this.cdr.markForCheck();
    }

    writeValue(value: AdvancedRule[]): void {
        this.initRelatedLists();

        this.subscriptionList.forEach(s => s.unsubscribe());
        this.subscriptionList = [];

        this.ruleFormArray.clear({emitEvent: false});

        if (arrayNotEmpty(value)) {
            value?.forEach(rule => this.ruleFormArray.push(this.advancedRuleLineToFormgroup(rule), {emitEvent: false}));
        }

        super.writeValue(value);
        this.cdr.markForCheck();
    }

    getIsOpenValue(ruleForm: FormGroup): boolean {
        return ruleForm.get('IsOpen').value;
    }

    showParametersCriteria(newGuid: string): void {
        this.ruleLineFormGroupList.forEach(c => {
            const isId = c.get('NewGuid').value === newGuid;
            const isOpenControl = c.get('IsOpen');
            isOpenControl.patchValue(isId
                ? !isOpenControl.value
                : isId
                , {emitEvent: isId}
            );
        });
    }

    validate(c: AbstractControl): Observable<ValidationErrors> {
        const ruleList: AdvancedRule[] = this.ruleFormArray.value;
        const {
            IdCritere,
            IdOperateur,
            Valeur,
            Subvention,
            ListeCalculsSubvention,
        } = ruleList[0] || {};

        if (this.ruleType === TypesRegleEnum.Subvention
            && arrayOnlyOne(ruleList)
            && !IdCritere
            && !IdOperateur
            && (!Valeur || Valeur === '0')
            && (!!Subvention || arrayNotEmpty(ListeCalculsSubvention))) {

            // cas spécial subvention on autorise une règle avancé ne contenant qu'une ligne avec subvention de rempli.
            return of(null);
        }

        return of(this.ruleFormArray.invalid ? {
            advancedruletableerrors: true,
            errorList: this.ruleFormArray.errors
        } : null);
    }

    getErrorMessages(errorObject: ValidationErrors): string[] {
        const errorList = [];
        Object.keys(errorObject).forEach(errorName => {
            this.errorMessages[errorName]
                ? errorList.push(this.errorMessages[errorName])
                : errorList.push(...this.otherMessage(errorName, errorObject[errorName]));
        });
        return errorList;
    }

    openAdvancedComputing(ruleForm: FormGroup, valueControlName: string, listControlName: string): void {
        const computationListControl = ruleForm.get(listControlName);

        const config = new KalDialogConfig<SharedAdvancedRuleComputationsDialogData>({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            title: 'Calcul avancé',
            data: {
                ruleComputationList: computationListControl.value || []
            }
        });

        this.watch$ = this.dialogService.open(SharedAdvancedRuleComputationsDialogComponent, config).afterClosed.pipe(
            filter(result => !!result?.accepted),
            tap(result => {
                ruleForm.patchValue({
                    [valueControlName]: null,
                    [listControlName]: result.ruleComputationList
                });
                this.cdr.markForCheck(); // nécessaire
            })
        );
    }

    removeRule(index: number): void {
        this.subscriptionList.splice(index, 1)[0].unsubscribe();
        this.ruleFormArray.removeAt(index);
    }

    duplicate(index: number): void {
        this.ruleFormArray.push(this.advancedRuleLineToFormgroup(this.normalizeLine(this.ruleFormArray.at(index).value)));
        this.cdr.markForCheck();
    }

    addRule(): void {
        this.ruleFormArray.push(this.advancedRuleLineToFormgroup());
        this.cdr.markForCheck();
    }

    moveDown(index: number): void {
        if (index === this.ruleFormArray.length - 1) {
            return;
        }

        const control = this.ruleFormArray.at(index);
        this.ruleFormArray.removeAt(index);
        this.ruleFormArray.insert(index + 1, control);
        this.cdr.markForCheck();
    }

    moveUp(index: number): void {
        if (index === 0) {
            return;
        }

        const control = this.ruleFormArray.at(index);
        this.ruleFormArray.removeAt(index);
        this.ruleFormArray.insert(index - 1, control);
        this.cdr.markForCheck();
    }

    hasParameters(criterionId: number): boolean {
        if (!criterionId) {
            return false;
        }

        return this.criteriaList.find(({Id}) => Id === criterionId)?.PossedeDesParametres;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.ruleFormArray.disable({emitEvent: false});
        } else {
            this.ruleFormArray.enable({emitEvent: false});
        }
        this.cdr.markForCheck();
    }

    private isCriterionOfCurrentRuleType(criterion: CritereModel): boolean {
        const {
            IsRegleAccessibilite,
            IsRegleEnveloppe,
            IsRegleQuotaNombre,
            IsRegleQuotaValeurFaciale,
            IsRegleSubvention
        } = criterion;
        switch (this.ruleType) {
            case TypesRegleEnum.Accessibilite:
                return IsRegleAccessibilite;
            case TypesRegleEnum.Enveloppe:
                return IsRegleEnveloppe;
            case TypesRegleEnum.QuotaFaciale:
                return IsRegleQuotaValeurFaciale;
            case TypesRegleEnum.QuotaNombre:
                return IsRegleQuotaNombre;
            case TypesRegleEnum.Subvention:
                return IsRegleSubvention;
            default:
                return false;
        }
    }

    private initRelatedLists(): void {
        const {
            applicationRuleList,
            ruleParamList,
            operatorList,
            criteriaList,
            dateTypeList
        } = this.advancedRuleService;

        this.operatorList = operatorList;
        this.ruleParamList = ruleParamList;
        this.applicationRuleList = applicationRuleList;
        this.dateTypeList = dateTypeList;
        this.regenerateOperatorListsByRuleType();
        this.setFilteredCriteriaList();
    }

    private regenerateOperatorListsByRuleType(): void {
        this.currentRuleTypeOperatorList = [
            TypesRegleEnum.Enveloppe,
            TypesRegleEnum.Quota,
            TypesRegleEnum.QuotaNombre,
            TypesRegleEnum.QuotaFaciale
        ].includes(this.ruleType)
            ? this.operatorList.filter(({
                                            TypeOperateur,
                                            Libelle
                                        }) => Libelle === '<=' || TypeOperateur === TypesOperateur.Logique)
            : this.operatorList;

        this.logicOperatorList = this.currentRuleTypeOperatorList.filter(({TypeOperateur}) => TypeOperateur === TypesOperateur.Logique);
        this.comparisonOperatorList = this.currentRuleTypeOperatorList.filter(({TypeOperateur}) => TypeOperateur === TypesOperateur.Comparaison);
    }

    private advancedRuleLineToFormgroup(rule: AdvancedRule = null): FormGroup {
        const hasFirst = this.ruleFormArray.length >= 1;
        const defaultOperatorId = hasFirst ? this.logicOperatorList?.[0]?.Id : null;
        const validatorOperatorId = !hasFirst ? [] : [FormValidator.isNotNull];

        const NewGuid = guid();
        const rawForm = this.fb.group({
            ParentheseOuvrante: ['('.repeat(rule?.ParentheseOuvrante || 0)],
            ParentheseFermante: [')'.repeat(rule?.ParentheseFermante || 0)],
            Parametres: [rule?.Parametres || []],
            Subvention: [rule?.Subvention || 0],
            SubventionDefaut: [rule?.SubventionDefaut || 0],
            SubventionGroupe: [rule?.SubventionGroupe || 0],
            SubventionDefautGroupe: [rule?.SubventionDefautGroupe || 0],
            IdCritere: [rule?.IdCritere || null, [FormValidator.isNotNull]],
            Valeur: [rule?.Valeur || null],
            ListeCalculs: [rule?.ListeCalculs || null],
            ListeCalculsSubvention: [rule?.ListeCalculsSubvention || null],
            ListeCalculsSubventionDefaut: [rule?.ListeCalculsSubventionDefaut || null],
            ListeCalculsSubventionGroupe: [rule?.ListeCalculsSubventionGroupe || null],
            ListeCalculsSubventionDefautGroupe: [rule?.ListeCalculsSubventionDefautGroupe || null],
            IdOperateur: [rule?.IdOperateur || defaultOperatorId || null, validatorOperatorId],
            IdOperateurMolecule: [rule?.IdOperateurMolecule || 0, [FormValidator.isNotNull]],
            IdTypeDate: [rule?.IdTypeDate || 0],
            IsGroupe: [rule?.IdOperateur || null],
            IdRegleApplication: [rule?.IdRegleApplication],
            IdRegleNonApplication: [rule?.IdRegleNonApplication],
            JourDuMois: [{day: rule?.Jours || null, month: rule?.Mois || null}],
            TypeRegle: [rule?.TypeRegle || this.ruleType],
            IsOpen: null,
            NewGuid
        }, {
            validators: [
                f => (isNil(f.get('Valeur').value) && arrayEmpty(f.get('ListeCalculs').value)) ? {novalue: true} : null
            ]
        });

        rawForm.get('IdOperateur').setValidators(c => this.ruleFormArray.at(0) === rawForm ? null : FormValidator.isNotNull(c));

        const {
            ParentheseOuvrante,
            ParentheseFermante,
            IdCritere,
            JourDuMois,
            IdTypeDate
        } = rawForm.controls;

        const subscriptionList: Observable<any>[] = [];
        [
            {control: ParentheseOuvrante, parenthese: '('},
            {control: ParentheseFermante, parenthese: ')'}
        ].forEach(({control, parenthese}) =>
            subscriptionList.push(control.valueChanges.pipe(
                tap(v => control.patchValue(!!v ? parenthese.repeat(v.length) : null, {emitEvent: false}))
            ))
        );



        subscriptionList.push(IdCritere.valueChanges.pipe(
            startWith(IdCritere.value),
            skip(1),
            tap(value => {
                const hasParameters = this.hasParameters(value);
                const criteria = !value ? null : (this.criteriaList || []).find(c => c.Id === value);
                const isEqualOperator = IsEqualOperatorPipe.format(this.criteriaList, value);
                const operatorList = FilteredComparisonOperatorListPipe.format(this.comparisonOperatorList, isEqualOperator);
                const isBoolField = criteria?.TypeDonnee?.Code === constants.fieldsTypesCodes.BOO;
                const equalOperatorId = isEqualOperator && arrayNotEmpty(operatorList) ? operatorList[0].Id : null;

                let newValeur = {
                    IsOpen: false,
                    Valeur: isBoolField ? 1 : null,
                    ListeCalculs: null,
                    IdOperateurMolecule: equalOperatorId || rawForm.get('IdOperateurMolecule').value,
                };

                rawForm.patchValue(newValeur, {emitEvent: false});

                if(hasParameters)
                {
                    this.showParametersCriteria(NewGuid);
                }
            })
        ));

        subscriptionList.push(IdTypeDate.valueChanges.pipe(
            startWith(IdTypeDate.value),
            tap(id => {
                const enable = ['SAI', 'SAIINF'].includes(this.dateTypeList.find(({Id}) => Id === id)?.Code);
                JourDuMois[enable ? 'enable' : 'disable']({emitEvent: false});

                this.cdr.markForCheck();
            })
        ));

        this.subscriptionList.push(merge(...subscriptionList).pipe(this.untilDestroyed).subscribe());
        return rawForm;
    }

    private parenthesisToNumber(parenthesis: number | string): number {
        if (typeof parenthesis === 'number') {
            return parenthesis ?? 0;
        }
        return (parenthesis as string)?.length || 0;
    }

    private otherMessage(errorName: string, value:any): string[] {
        switch (errorName) {
            case  'missingcloseparenthesis':
                return [`Vous n'avez pas fermé toutes les parenthèses. Il manque ${value} parenthèse${value > 1 ? 's' : ''}.`];
            case  'missingopenparenthesis':
                return [`Vous avez inséré trop de parenthèses ouvrantes. Il y a ${value} parenthèse${value > 1 ? 's' : ''} en trop.`];
            case  'missingcloseparenthesisbyline':
                return value.map(error => {
                    const nb = Math.abs(error[1]);
                    return `Règle n°${error[0] + 1}. Impossible de fermer le groupe de parenthèses. Il manque ${nb} parenthèse${nb > 1 ? 's' : ''}.`;
                });
            case  'applicationRulesbyline':
                return value.map(error => {
                    return `Règle n°${error + 1}. Une règle ne peut s'appliquer qu'à un groupe de règles ou bien sur une règle autonome.`;
                });
            default :
                return ['Erreur inconnue.'];
        }
    }

    private globalFormArrayChecks(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const rules: AdvancedRule[] = control.value;

            if (arrayEmpty(rules)) {
                return {emptyadvancedrule: true};
            }

            const errors: ValidationErrors = {};
            const parenthesisCloseErrors: indexToNumberTuple[] = [];
            const applicationRulesErrors: number[] = [];

            let parenthesisIntegrity = 0;

            rules.forEach((rule, index) => {
                parenthesisIntegrity += this.parenthesisToNumber(rule.ParentheseOuvrante);
                parenthesisIntegrity -= this.parenthesisToNumber(rule.ParentheseFermante);

                if (parenthesisIntegrity < 0) {
                    parenthesisCloseErrors.push([index, parenthesisIntegrity]);
                } else if (parenthesisIntegrity > 0 && (rule.IdRegleApplication || rule.IdRegleNonApplication)) {
                    applicationRulesErrors.push(index);
                }

                if (this.ruleType !== TypesRegleEnum.Subvention) {
                    if (!rule.IdOperateurMolecule) {
                        errors.nooperatorvalue = true;
                    }

                    if (!rule.IdCritere) {
                        errors.nocriteria = true;
                    }

                    if (!rule.Valeur && !rule.ListeCalculs) {
                        errors.novalue = true;
                    }
                } else {
                    // Dans le contexte d'une subvention, on utilise pas les erreurs de critères <<standard>>
                    ['nooperatorvalue', 'nocriteria', 'novalue'].forEach(errorProperty => {
                        if(errors[errorProperty]) {
                            delete errors[errorProperty];
                        }
                    });

                    if(!rule.IdCritere
                        && !rule.IdOperateurMolecule
                        && (!rule.Valeur && rule.Valeur !== '0')
                        && !rule.Subvention && arrayEmpty(rule.ListeCalculsSubvention)) {
                        errors.nosubvention = true;
                    } else {
                        if((!rule.Subvention && arrayEmpty(rule.ListeCalculsSubvention))
                            && (!rule.IdCritere || !rule.IdOperateurMolecule || (!rule.Valeur && rule.Valeur !== '0'))) {
                            errors.nocriteriavalue = true;
                        }
                    }
                }
            });

            if (parenthesisIntegrity < 0) {
                errors.missingcloseparenthesis = -parenthesisIntegrity;
            } else if (parenthesisIntegrity > 0) {
                errors.missingopenparenthesis = parenthesisIntegrity;
            }


            if (arrayNotEmpty(parenthesisCloseErrors)) {
                errors.missingcloseparenthesisbyline = parenthesisCloseErrors;
            }
            if (arrayNotEmpty(applicationRulesErrors)) {
                errors.applicationRulesbyline = applicationRulesErrors;
            }

            return arrayNotEmpty(Object.keys(errors)) ? errors : null;
        };
    }

    private normalizeLine(line: FormAdvancedRule): AdvancedRule {
        const {
            ParentheseOuvrante,
            ParentheseFermante,
            JourDuMois,
            ...rule
        } = line;

        return {
            ...rule,
            ParentheseOuvrante: this.parenthesisToNumber(ParentheseOuvrante),
            ParentheseFermante: this.parenthesisToNumber(ParentheseFermante),
            Jours: JourDuMois?.day || 0,
            Mois: JourDuMois?.month || 0,
        };
    }

    private normalize(value: FormAdvancedRule[]): AdvancedRule[] {
        return value.map(rule => this.normalizeLine(rule));
    }

    ngOnInit(): void {
        this.ruleFormArray = this.fb.array([], {
            validators: [this.globalFormArrayChecks()]
        });

        this.ruleFormArray.controls.forEach(element => {
            this.showParametersCriteria(element.get('IdCritere').value);
        });

        this.watch$ = this.ruleFormArray.valueChanges.pipe(tap(v => this.notifyUpdate(this.normalize(v))));
    }
}
